import { inject } from '@angular/core';
import { RedirectFunction, Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { PARTNER_SLUG_PARAM, partnerSlugGuard, partnerSlugMatchGuard } from './core/guards/partner-slug.guard';
import { PartnerContextService } from './core/services/partner-context.service';
import { MainLayout } from './layout/main-layout/main-layout';

/**
 * The bare root is not an address of its own: every page of the panel belongs to a partner,
 * so `/` forwards to the partner this browser used last (the default one on a fresh browser).
 */
const toRememberedPartner: RedirectFunction = () => `/${inject(PartnerContextService).rememberedSlug}`;

/**
 * Every route lives under a partner segment (`/acme/login`, `/acme`, `/acme/reports`), which is what
 * lets one deployment serve every partner and gives each of them a bookmarkable address.
 *
 * `partnerSlugMatchGuard` keeps addresses that cannot be a partner out of the panel, and
 * `partnerSlugGuard` publishes the slug before anything below it is activated — `authGuard`
 * then decides whether the stored session belongs to THIS partner.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: toRememberedPartner
  },
  {
    path: `:${PARTNER_SLUG_PARAM}`,
    canMatch: [partnerSlugMatchGuard],
    canActivate: [partnerSlugGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login').then(m => m.LoginComponent)
      },
      {
        path: '',
        component: MainLayout,
        canActivate: [authGuard],
        children: [
          // The panel's main page: issue licences. Reports open from a button on it.
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features/licenses/create-license/create-license.component').then(
                m => m.CreateLicenseComponent
              )
          },
          {
            path: 'reports',
            loadComponent: () =>
              import('./features/reports/reports.component').then(m => m.ReportsComponent)
          },
          // Address of the create screen before 2.1 (bookmarks, the support panel's links).
          {
            path: 'licenses/create',
            redirectTo: ''
          },
          // An unknown page under a known partner is still that partner's panel.
          {
            path: '**',
            redirectTo: ''
          }
        ]
      }
    ]
  },
  {
    path: '**',
    loadComponent: () =>
      import('./features/partner-not-found/partner-not-found').then(m => m.PartnerNotFoundComponent)
  }
];
